import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import DesignIconConstants from '~/constants/DesignIconConstants';
import {
  deleteHandleIcon,
  editHandleIcon,
  resizeHandleIcon
} from '~/constants/InlineSvgs';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';

import {isNamesOrNumbers, isText} from '~/data/accessors/icon';
import {
  move as moveIcon,
  resize as resizeIcon,
  textFitToDimension
} from '~/data/mutators/icon';
import Color from '~/data/records/Color';
import {roundDecimal} from '~/shared/utils/math';
import {invert} from '~/utils/colors';
import renderInlineSvg from '~/utils/renderInlineSvg';
import {doesIntersect} from '~/utils/geometry';

import BoundingBox from '~/components/BoundingBox';
import DesignIcon from '~/components/DesignIcon';
import Guideline from '~/components/Guideline';
import MoveWithKeys from '~/utils/components/MoveWithKeys';
import Gesturable from '~/shared/components/utils/Gesturable';
import SelectionArea from '~/components/SelectionArea';
import {CopyIcon, Tooltip, IconButton} from '@customink/pigment-react';
import {metrics} from '~/vendor/metrics';

import './index.scss';

const CENTER_STICKINESS = 5;

const MINIMUM_ICON_SIZE = 0.1;

const hypotenuse = (x, y) => Math.sqrt(x * x + y * y);

const normalizeValueToRange = (value, min, max) => {
  return Math.max(min, Math.min(max, value));
};

const resizeRectRatio_distance = (rect, startingRect) => {
  const base_distance = hypotenuse(startingRect.width, startingRect.height);
  const resize_distance = hypotenuse(rect.height, rect.width);
  const resizeRatio = resize_distance / base_distance;
  return {
    ...rect,
    height: startingRect.height * resizeRatio,
    width: startingRect.width * resizeRatio
  };
};

const sanitizeMinRectSize = (rect, minRect) => {
  return {
    ...rect,
    width: Math.max(rect.width, minRect.width),
    height: Math.max(rect.height, minRect.height)
  };
};

const sanitizeMaxRectSize = (rect, maxRect) => {
  return {
    ...rect,
    width: Math.min(rect.width, maxRect.width),
    height: Math.min(rect.height, maxRect.height)
  };
};

const fitRectProportionaly = (rect, bounds) => {
  const rectRatio = rect.width / rect.height;
  const boundsRatio = bounds.width / bounds.height;

  if (rectRatio > boundsRatio) {
    return {
      width: bounds.width,
      height: rect.height * (bounds.width / rect.width)
    };
  } else {
    return {
      width: rect.width * (bounds.height / rect.height),
      height: bounds.height
    };
  }
};

const noop = () => {};

const propTypes = {
  productColor: PropTypes.instanceOf(Color).isRequired,
  selectedViewId: PropTypes.number.isRequired,
  activateIntersectingIconButton: PropTypes.func,
  afterDelete: PropTypes.func,
  blankDesign: PropTypes.bool,
  centeredDesign: PropTypes.bool,
  deactivateIntersectingIconButton: PropTypes.func,
  duplicate: PropTypes.func,
  guidelines: PropTypes.arrayOf(PropTypes.object),
  handleOpenIconTools: PropTypes.func,
  handleSelectIcon: PropTypes.func,
  handleSelectIcons: PropTypes.func,
  iconError: PropTypes.func,
  iconRendered: PropTypes.func,
  icons: PropTypes.arrayOf(PropTypes.object),
  // icon chrome pass throughs
  interactive: PropTypes.bool,
  intersectingIcons: PropTypes.object,
  isCircularEmbroideryTestEnabled: PropTypes.bool,
  main: PropTypes.bool,
  move: PropTypes.func,
  multiItemCenteredDesign: PropTypes.bool,
  multiItemOverlay: PropTypes.bool,
  productConstraint: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  productRendered: PropTypes.bool,
  remove: PropTypes.func,
  repositionForTextEffects: PropTypes.func,
  resize: PropTypes.func,
  scale: PropTypes.number,
  selectedIconId: PropTypes.string,
  selectedIconIds: PropTypes.arrayOf(PropTypes.string),
  selectedIconType: PropTypes.string,
  selectedViewScaleFactor: PropTypes.number,
  updateFontSize: PropTypes.func,
  zoomConstraint: PropTypes.shape({
    height: PropTypes.number,
    left: PropTypes.number,
    marginLeft: PropTypes.number,
    marginTop: PropTypes.number,
    position: PropTypes.string,
    top: PropTypes.number,
    width: PropTypes.number
  })
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export default class Design extends Component {
  static displayName = 'Design';
  static propTypes = propTypes;

  static defaultProps = {
    centeredDesign: false,
    guidelines: [],
    iconError: noop,
    iconRendered: noop,
    multiItemCenteredDesign: false,
    selectedIconIds: []
  };

  state = {
    hasIconNearCenter: false,
    icons: [],
    isBusy: false,
    selectionAreaRect: null,
    iconsInSelectionArea: []
  };

  UNSAFE_componentWillMount() {
    this.updateIconsFromProps(this.props);
  }

  componentDidMount() {
    document.body.addEventListener('mousedown', this.handleMouseDown);
    document.body.addEventListener('touchstart', this.handleMouseDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.updateIconsFromProps(nextProps);
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.productRendered;
  }

  componentDidUpdate(prevProps) {
    const {icons: prevIcons, selectedIconId: prevSelectedIconId} = prevProps;
    const {icons, selectedIconId, intersectingIcons} = this.props;

    this.checkForIntersectionAfterIconUpdate({
      selectedIconId,
      prevSelectedIconId,
      prevIcons,
      icons,
      intersectingIcons
    });
  }

  componentWillUnmount() {
    document.body.removeEventListener('mousedown', this.handleMouseDown);
    document.body.removeEventListener('touchstart', this.handleMouseDown);
  }

  updateIconsFromProps(props) {
    this.setState({icons: props.icons});
  }

  checkForIntersectionAfterIconUpdate({
    prevSelectedIconId,
    selectedIconId,
    prevIcons,
    icons,
    intersectingIcons
  }) {
    if (!selectedIconId || !icons || !intersectingIcons) {
      return;
    }

    const selectedIcon = icons.find(i => i?.id === selectedIconId);
    const prevSelectedIcon =
      prevIcons && prevSelectedIconId
        ? prevIcons.find(i => i?.id === prevSelectedIconId)
        : null;

    if (selectedIcon !== prevSelectedIcon) {
      this.dispatchInterceptingIconsOnPossibleOverlapping();
    }
  }

  moveCache = null;
  resizeCache = null;

  getSelectedIcons = () => {
    return this.state.icons.filter(icon =>
      this.props.selectedIconIds.includes(icon.id)
    );
  };

  getCurrentViewIcons = () => {
    return this.state.icons.filter(
      icon => icon.viewId === this.props.selectedViewId
    );
  };

  getIconsToRender = () => {
    // Picks icons from the current view and icons that need to be prefetched
    return this.state.icons.filter(icon => {
      return icon.viewId === this.props.selectedViewId || !icon.width;
    });
  };

  getBorderColor = () => {
    return invert(this.props.productColor).cssString;
  };

  setNearCenter = value => {
    this.setState({hasIconNearCenter: value});
  };

  iconAtMinScale = (icon, scaleX) => {
    return isText(icon.type) && icon.fontHeight * scaleX <= icon.fontMinHeight;
  };

  getCenter = () => ({
    left: roundDecimal(this.props.zoomConstraint.width / 2),
    top: roundDecimal(this.props.zoomConstraint.height / 2)
  });

  getIconRect = (icon, scale, origin = null) => {
    const rect = {
      id: icon.id,
      top: roundDecimal((icon.y - icon.height / 2) * scale),
      left: roundDecimal((icon.x - icon.width / 2) * scale),
      width: roundDecimal(icon.width * scale),
      height: roundDecimal(icon.height * scale)
    };

    if (origin) {
      rect.top += origin.top;
      rect.left += origin.left;
    }

    if (
      rect.left < 0 &&
      this.props.centeredDesign &&
      !this.props.multiItemCenteredDesign
    ) {
      rect.left = 0;
    }

    return rect;
  };

  getScreenIconRect = icon => {
    const scale = this.props.scale;
    const origin = this.getCenter();

    return this.getIconRect(icon, scale, origin);
  };

  getSelectionRect = icons => {
    const rects = icons.map(this.getScreenIconRect);

    const left = Math.min(...rects.map(rect => rect.left));
    const top = Math.min(...rects.map(rect => rect.top));
    const right = Math.max(...rects.map(rect => rect.left + rect.width));
    const bottom = Math.max(...rects.map(rect => rect.top + rect.height));
    const width = right - left;
    const height = bottom - top;

    return {left, top, width, height};
  };

  applyIconTransforms = transforms => {
    return this.props.icons.map(icon => {
      const transformation = transforms.find(i => i.icon.id === icon.id);

      if (transformation) {
        const {move, scale} = transformation;

        if (scale) {
          icon = resizeIcon(icon, scale.x, scale.y);
        }

        if (move) {
          icon = moveIcon(icon, move.x, move.y);
        }
      }

      return icon;
    });
  };

  snapToCenter = (rect, screenDeltaX) => {
    const origin = this.getCenter();
    const rectMidpointX = rect.left + rect.width / 2;
    const distanceFromOrigin = origin.left - rectMidpointX;

    if (Math.abs(distanceFromOrigin - screenDeltaX) < CENTER_STICKINESS) {
      this.setNearCenter(true);
      return distanceFromOrigin;
    } else {
      this.setNearCenter(false);
      return screenDeltaX;
    }
  };

  handleMouseDown = e => {
    if (this.state.isBusy) {
      e.stopPropagation();
    }
  };

  getMoveDeltaBoundaries = selectionRect => {
    const {scale, productConstraint} = this.props;

    const min = {
      x: -selectionRect.left,
      y: -selectionRect.top
    };

    const max = {
      x:
        productConstraint.width * scale -
        (selectionRect.left + selectionRect.width),
      y:
        productConstraint.height * scale -
        (selectionRect.top + selectionRect.height)
    };

    return {min, max};
  };

  handleMoveStart = () => {
    const selectedIcons = this.getSelectedIcons();
    const selectionRect = this.getSelectionRect(selectedIcons);

    this.moveCache = {
      startSelectionRect: selectionRect,
      selectedIcons: selectedIcons,
      offsetBoundaries: this.getMoveDeltaBoundaries(selectionRect),
      transforms: []
    };

    this.setState({
      selectionRect,
      isBusy: true
    });
  };

  handleMove = ({deltaX, deltaY}, snapToCenter = true) => {
    const {scale} = this.props;

    if (this.moveCache === null) {
      return;
    }

    const {
      startSelectionRect,
      selectedIcons,
      offsetBoundaries: {min, max}
    } = this.moveCache;

    const screenDelta = {
      x: snapToCenter ? this.snapToCenter(startSelectionRect, deltaX) : deltaX,
      y: deltaY
    };

    screenDelta.x = normalizeValueToRange(screenDelta.x, min.x, max.x);
    screenDelta.y = normalizeValueToRange(screenDelta.y, min.y, max.y);

    const printDelta = {
      x: screenDelta.x / scale,
      y: screenDelta.y / scale
    };

    const transforms = (this.moveCache.transforms = selectedIcons.map(icon => ({
      icon,
      move: printDelta
    })));

    const icons = this.applyIconTransforms(transforms);

    this.setState({
      icons
    });
  };

  handleMoveStop = (toogleBusy = true) => {
    const stateUpdates = {};

    if (this.moveCache) {
      this.props.move({
        transforms: this.moveCache.transforms,
        constraints: this.props.productConstraint
      });

      this.moveCache = null;

      stateUpdates.icons = this.props.icons;
    }

    if (toogleBusy) {
      stateUpdates.isBusy = false;
    }

    this.setState(stateUpdates);
  };

  dispatchInterceptingIconsOnPossibleOverlapping = () => {
    const {
      selectedIconId,
      activateIntersectingIconButton,
      deactivateIntersectingIconButton,
      intersectingIcons
    } = this.props;
    let iconRects = this.getIconRects();

    if (
      !selectedIconId ||
      !activateIntersectingIconButton ||
      !deactivateIntersectingIconButton ||
      !intersectingIcons
    ) {
      return;
    }

    const selectedIconRect = iconRects.find(item => {
      return item?.id === selectedIconId;
    });

    if (!selectedIconRect) {
      return;
    }

    iconRects = iconRects.filter(item => {
      return item?.id !== selectedIconRect.id;
    });

    const iconsToActivateIntersectionFlag = [];
    const iconsToDeactivateIntersectionFlag = [];
    let selectedIconRectHasIntersected = false;

    iconRects.forEach(iconRect => {
      if (doesIntersect(selectedIconRect, iconRect)) {
        selectedIconRectHasIntersected = true;
        iconsToActivateIntersectionFlag.push(iconRect?.id);
      } else {
        iconsToDeactivateIntersectionFlag.push(iconRect?.id);
      }
    });

    if (selectedIconRectHasIntersected) {
      iconsToActivateIntersectionFlag.push(selectedIconRect.id);
    } else {
      iconsToDeactivateIntersectionFlag.push(selectedIconRect.id);
    }

    if (
      this.isReorderActionRequired({
        intersectingIcons: intersectingIcons,
        listIconsIntersectingState: iconsToActivateIntersectionFlag,
        requireActionFlag: false
      })
    ) {
      activateIntersectingIconButton?.(iconsToActivateIntersectionFlag);
    }

    if (
      this.isReorderActionRequired({
        intersectingIcons: intersectingIcons,
        listIconsIntersectingState: iconsToDeactivateIntersectionFlag,
        requireActionFlag: true
      })
    ) {
      deactivateIntersectingIconButton?.(iconsToDeactivateIntersectionFlag);
    }
  };

  isReorderActionRequired({
    intersectingIcons,
    listIconsIntersectingState,
    requireActionFlag
  }) {
    if (!listIconsIntersectingState || !intersectingIcons) {
      return false;
    }

    let requireAction = false;

    for (let i = 0; i < listIconsIntersectingState.length; i++) {
      const iconId = listIconsIntersectingState[i];

      if (typeof intersectingIcons[iconId] === 'undefined') {
        requireAction = true;
        break;
      }

      if (intersectingIcons[iconId] === requireActionFlag) {
        requireAction = true;
        break;
      }
    }

    return requireAction;
  }

  handleMoveByKey = ({deltaX, deltaY}) => {
    this.handleMoveStart();
    this.handleMove({deltaX, deltaY}, false);
    this.handleMoveStop(false);
  };

  getPrintOffsetInSelection = (icon, selectionRect) => {
    const scale = this.props.scale;
    const iconRect = this.getScreenIconRect(icon);

    return {
      x: (iconRect.left - selectionRect.left) / scale + icon.width / 2,
      y: (iconRect.top - selectionRect.top) / scale + icon.height / 2
    };
  };

  getMinSelectionRect = (selectionRect, icons) => {
    const getIconMinSizeRatio = icon => {
      if (isText(icon.type) && !textFitToDimension(icon)) {
        return icon.fontMinHeight / icon.fontHeight;
      }

      return MINIMUM_ICON_SIZE / Math.min(icon.width, icon.height);
    };

    const minIconSizeRatio = Math.max(...icons.map(getIconMinSizeRatio));

    const minSize = {
      width: minIconSizeRatio * selectionRect.width,
      height: minIconSizeRatio * selectionRect.height
    };

    return minSize;
  };

  getMaxSelectionRect = (rect, hasLockRatio) => {
    const {scale, productConstraint} = this.props;

    let maxSize = {
      width: productConstraint.width * scale - rect.left,
      height: productConstraint.height * scale - rect.top
    };

    if (hasLockRatio) {
      maxSize = fitRectProportionaly(rect, maxSize);
    }

    return maxSize;
  };

  shouldLockRatio = icons => {
    if (icons.length > 1) {
      return true;
    }
    const icon = icons[0];
    return (
      (icon.lockRatio && !textFitToDimension(icon)) ||
      (isText(icon.type) && !textFitToDimension(icon))
    );
  };

  handleSelectionArea = rect => {
    const {zoomConstraint} = this.props;

    rect = {
      left: rect.left - zoomConstraint.left,
      top: rect.top - zoomConstraint.top,
      width: rect.width,
      height: rect.height
    };

    const iconsInSelectionArea = this.getIntersectingIcons(rect);

    this.setState({
      iconsInSelectionArea
    });
  };

  handleSelectionAreaStop = () => {
    this.props.handleSelectIcons(this.state.iconsInSelectionArea);

    this.setState({
      iconsInSelectionArea: []
    });
  };

  getIntersectingIcons = rect => {
    return this.getCurrentViewIcons().filter(icon => {
      const iconRect = this.getIconRect(
        icon,
        this.props.scale,
        this.getCenter()
      );
      return doesIntersect(iconRect, rect);
    });
  };

  handleResizeStart = e => {
    e.preventDefault();

    const icons = this.getSelectedIcons();
    const selectionRect = this.getSelectionRect(icons);
    const hasLockRatio = this.shouldLockRatio(icons);
    const minSelectionRect = this.getMinSelectionRect(selectionRect, icons);
    const maxSelectionRect = this.getMaxSelectionRect(
      selectionRect,
      hasLockRatio
    );

    const items = icons.map(icon => ({
      printOffsetInSelection: this.getPrintOffsetInSelection(
        icon,
        selectionRect
      ),
      icon
    }));

    this.resizeCache = {
      startSelectionRect: selectionRect,
      hasLockRatio: hasLockRatio,
      transforms: [],
      minSelectionRect,
      maxSelectionRect,
      items
    };

    this.setState({
      isBusy: true,
      selectionRect
    });
  };

  handleResizeStop = () => {
    this.setState({
      icons: this.props.icons,
      isBusy: false
    });

    this.props.resize({
      transforms: this.resizeCache?.transforms,
      constraints: this.props.productConstraint,
      source: 'drag'
    });

    this.resizeCache = null;
  };

  handleResize = ({deltaX, deltaY}) => {
    const {
      startSelectionRect,
      hasLockRatio,
      minSelectionRect,
      maxSelectionRect,
      items
    } = this.resizeCache;

    let selectionRect = {
      ...startSelectionRect,
      width: startSelectionRect.width + deltaX,
      height: startSelectionRect.height + deltaY
    };

    if (hasLockRatio) {
      selectionRect = resizeRectRatio_distance(
        selectionRect,
        startSelectionRect
      );
    }

    // Normalize selection rect to fit within min/max contraints
    selectionRect = sanitizeMinRectSize(selectionRect, minSelectionRect);
    selectionRect = sanitizeMaxRectSize(selectionRect, maxSelectionRect);

    const scaleRatio = {
      x: selectionRect.width / startSelectionRect.width,
      y: selectionRect.height / startSelectionRect.height
    };

    const transforms = (this.resizeCache.transforms = items.map(
      ({icon, printOffsetInSelection}) => {
        const printOffset = {
          x: (scaleRatio.x - 1) * printOffsetInSelection.x,
          y: (scaleRatio.y - 1) * printOffsetInSelection.y
        };

        return {
          icon,
          move: printOffset,
          scale: scaleRatio
        };
      }
    ));

    const icons = this.applyIconTransforms(transforms);

    this.setState({
      icons
    });
  };

  handleDuplicate = () => {
    // Icon type 'clipart' needs to be tagged as 'art' for metrics, 'upload'/'text' otherwise.
    const iconType =
      this.props.selectedIconType === DesignIconConstants.TYPES.clipart
        ? 'art'
        : this.props?.selectedIconType?.toLowerCase();
    metrics.pageViewTag(`ndx/${iconType}/duplicate_icon`);
    this.props.duplicate({source: 'canvas', iconType: iconType});
  };

  renderGuidelines = () =>
    this.props.guidelines.map((guideline, i) => (
      <Guideline
        key={guideline.name || i}
        borderColor={this.getBorderColor()}
        guideline={guideline}
        circular={
          this.props.isCircularEmbroideryTestEnabled &&
          guideline.width === guideline.height
        }
        isVisible={this.state.isBusy}
      />
    ));

  setBusy = isBusy => {
    if (this.state.isBusy !== isBusy) {
      this.setState({isBusy});
    }
  };

  scaleIcons = (iconRects, factor) => {
    const scaledIconRects = iconRects.map(iconRect => {
      iconRect.width *= factor;
      iconRect.height *= factor;
      iconRect.top = iconRect.top * factor;
      iconRect.left = iconRect.left * factor;
      return iconRect;
    });
    return scaledIconRects;
  };

  scaleIconsVertically = iconRects => {
    const heightConstraint = this.props.zoomConstraint.height;
    const minTop = Math.min(...iconRects.map(iconRect => iconRect.top));
    const maxTop = Math.max(
      ...iconRects.map(iconRect => iconRect.top + iconRect.height)
    );
    const height = maxTop - minTop;
    const heightFactor =
      height > heightConstraint ? heightConstraint / height : 1;
    return this.scaleIcons(iconRects, heightFactor);
  };

  scaleIconsToFitConstraint = iconRects => {
    const heightConstraint = this.props.zoomConstraint.height;
    const verticallyScaledIcons = this.scaleIconsVertically(iconRects);

    const minLeft = Math.min(
      ...verticallyScaledIcons.map(iconRect => iconRect.left)
    );
    const maxLeft = Math.max(
      ...verticallyScaledIcons.map(iconRect => iconRect.left + iconRect.width)
    );
    const width = maxLeft - minLeft;
    const widthFactor = width > heightConstraint ? heightConstraint / width : 1;

    return width > heightConstraint
      ? this.scaleIcons(verticallyScaledIcons, widthFactor)
      : verticallyScaledIcons;
  };

  centerScaledIcons = iconRects => {
    const minTop = Math.min(...iconRects.map(iconRect => iconRect.top));
    const maxTop = Math.max(
      ...iconRects.map(iconRect => iconRect.top + iconRect.height)
    );
    const height = maxTop - minTop;
    const topOffsetFactor = (this.props.zoomConstraint.height - height) / 2;
    const topOffset = topOffsetFactor - minTop;

    const minLeft = Math.min(...iconRects.map(iconRect => iconRect.left));
    const maxLeft = Math.max(
      ...iconRects.map(iconRect => iconRect.left + iconRect.width)
    );
    const width = maxLeft - minLeft;
    const leftOffsetFactor = (this.props.zoomConstraint.width - width) / 2;
    const leftOffset = leftOffsetFactor - minLeft;

    iconRects = iconRects.map(iconRect => {
      iconRect.top += topOffset;
      iconRect.left += leftOffset;
      return iconRect;
    });

    return iconRects;
  };

  renderCenteredMiniIcons = iconRects => {
    const scaledIconRects = this.scaleIconsToFitConstraint(iconRects);
    return this.centerScaledIcons(scaledIconRects);
  };

  renderIcons = () => {
    if (!this.props.productRendered) {
      return null;
    }

    const iconRects = this.getIconRects();

    return this.getIconsToRender().map((icon, index) => {
      const rect = iconRects[index];
      const designIconStyle = {
        position: 'absolute',
        top: rect.top,
        left: rect.left,
        height: rect.height,
        width: rect.width
      };

      return (
        <DesignIcon
          key={`di-${icon.id}`}
          icon={icon}
          iconError={this.props.iconError}
          iconRect={rect}
          iconRects={iconRects.filter((_, i) => i !== index)}
          iconRendered={this.props.iconRendered}
          repositionForTextEffects={this.props.repositionForTextEffects}
          rerender={!this.state.isBusy}
          scale={this.props.scale}
          selectedViewScaleFactor={this.props.selectedViewScaleFactor}
          style={designIconStyle}
        />
      );
    });
  };

  getIconRects = () => {
    let iconRects = this.getIconsToRender().map(icon =>
      this.getScreenIconRect(icon)
    );

    if (this.props.multiItemCenteredDesign) {
      iconRects = this.renderCenteredMiniIcons(iconRects);
    }

    if (this.props.centeredDesign) {
      let minTop = Math.min(...iconRects.map(iconRect => iconRect.top));
      if (minTop < 0) {
        iconRects = iconRects.map(iconRect => {
          iconRect.top += Math.abs(minTop);
          return iconRect;
        });
        minTop = 0;
      }
      const minBottom = Math.max(
        ...iconRects.map(iconRect => iconRect.top + iconRect.height)
      );
      const topOffset = (this.props.zoomConstraint.height - minBottom) / 2;

      iconRects = iconRects.map(iconRect => {
        iconRect.top += topOffset;
        return iconRect;
      });
    }

    if (this.props.multiItemOverlay) {
      iconRects = iconRects.map(iconRect => {
        if (iconRect.left < 0) {
          iconRect.left = 0;
        } else {
          if (
            iconRect.left + iconRect.width >
            this.props.zoomConstraint.width
          ) {
            const difference =
              iconRect.left + iconRect.width - this.props.zoomConstraint.width;
            iconRect.left -= difference;
          }
        }
        return iconRect;
      });
    }

    return iconRects;
  };

  renderBoundingBoxes = () => {
    const {iconsInSelectionArea} = this.state;

    return this.getCurrentViewIcons().map((icon, index) => {
      const isInSelectionArea = iconsInSelectionArea.some(
        i => i.id === icon.id
      );
      const isSelected = this.props.selectedIconIds.includes(icon.id);
      const rect = this.getScreenIconRect(icon);

      const selectIcon = toggle => {
        this.props.handleSelectIcon(icon, toggle);
      };

      return (
        <Gesturable
          key={`bb-${icon.id}-${index}`}
          gesture="drag"
          onMove={this.handleMove}
          onStart={this.handleMoveStart}
          onStop={this.handleMoveStop}>
          <BoundingBox
            isInSelectionArea={isInSelectionArea}
            isSelected={isSelected}
            rect={rect}
            onSelect={selectIcon}
          />
        </Gesturable>
      );
    });
  };

  renderSelectionBoundingBox = () => {
    const {isBusy} = this.state;
    const {selectedIconIds} = this.props;
    const isMobile = breakpoint(MediaQueries.mediumAndBelow);
    const showResizeTooltip = !isMobile && !this.state.isBusy;

    // Selected icons are filtered from the state, instead the props, to contain ongoing transformations.
    const selectedIcons = this.state.icons.filter(icon => {
      return selectedIconIds.includes(icon.id) && icon.rendered;
    });

    if (!selectedIcons.length) {
      return;
    }

    const {type} = selectedIcons[0];

    const canEdit =
      breakpoint(MediaQueries.mediumAndBelow) &&
      selectedIcons.length === 1 &&
      !(
        type === DesignIconConstants.TYPES.clipart ||
        type === DesignIconConstants.TYPES.upload ||
        type === DesignIconConstants.TYPES.text
      );

    const canResize = selectedIcons.every(icon => !isNamesOrNumbers(icon.type));
    const canDuplicate = canResize;
    const selectionRect = this.getSelectionRect(selectedIcons);

    const classes = classnames('ndx-Design-selection', {isBusy});
    const style = {...selectionRect};

    return (
      <MoveWithKeys
        onMoveKeyDown={this.handleMoveByKey}
        onMoveKeyUp={this.handleMoveStop}>
        <div
          className={classes}
          style={style}
          onMouseDown={e => e.stopPropagation()}>
          <Gesturable
            gesture="drag"
            onMove={this.handleMove}
            onStart={this.handleMoveStart}
            onStop={this.handleMoveStop}>
            <div className="ndx-Design-selection-dragzone" />
          </Gesturable>

          <Tooltip
            arrow={false}
            placement="top"
            title="Delete"
            className="ndx-Design-selection-handle isDelete"
            onClick={this.props.remove}>
            <IconButton>
              <div
                className="ndx-Design-selection-handle"
                dangerouslySetInnerHTML={renderInlineSvg(deleteHandleIcon)}
              />
            </IconButton>
          </Tooltip>

          {canResize && (
            <Gesturable
              gesture="drag"
              threshold={0}
              onMove={this.handleResize}
              onStart={this.handleResizeStart}
              onStop={this.handleResizeStop}>
              <Tooltip
                arrow={false}
                placement="top"
                title={showResizeTooltip && 'Resize'}
                className="ndx-Design-selection-handle isResize">
                <IconButton>
                  <div
                    className="ndx-Design-selection-handle"
                    dangerouslySetInnerHTML={renderInlineSvg(resizeHandleIcon)}
                    onMouseDown={() => this.setBusy(true)}
                    onMouseUp={() => this.setBusy(false)}
                  />
                </IconButton>
              </Tooltip>
            </Gesturable>
          )}

          {canEdit && (
            <Tooltip
              arrow={false}
              placement="top"
              title="Edit"
              className="ndx-Design-selection-handle isEdit"
              onClick={() => this.props.handleOpenIconTools(selectedIcons[0])}>
              <IconButton>
                <div
                  className="ndx-Design-selection-handle"
                  dangerouslySetInnerHTML={renderInlineSvg(editHandleIcon)}
                />
              </IconButton>
            </Tooltip>
          )}

          {canDuplicate && (
            <Tooltip
              arrow={false}
              placement="top"
              title="Duplicate"
              className="ndx-Design-selection-handle isDuplicate"
              onClick={this.handleDuplicate}>
              <IconButton>
                <CopyIcon className="ndx-Design-selection-handle" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </MoveWithKeys>
    );
  };

  render() {
    const {zoomConstraint, main} = this.props;
    const {isBusy, hasIconNearCenter} = this.state;

    const style = {
      left: zoomConstraint.left,
      top: zoomConstraint.top,
      width: zoomConstraint.width,
      height: zoomConstraint.height,
      color: this.getBorderColor(),
      marginLeft: zoomConstraint.marginLeft,
      marginTop: zoomConstraint.marginTop,
      position: zoomConstraint.position
    };

    const printableAreaClassName = classnames('ndx-Design-printableArea', {
      isBusy
    });
    const centerLineClassName = classnames('ndx-Design-centerLine', {
      isVisible: isBusy && hasIconNearCenter
    });

    if (main) {
      return (
        <div className="ndxndx-CanvasContainer-pageContainer-Design">
          {this.renderGuidelines()}

          <SelectionArea
            isDisabled={this.state.isBusy || !this.props.main}
            onSelection={this.handleSelectionArea}
            onStop={this.handleSelectionAreaStop}
          />

          <div
            ref={node => {
              this.printableAreaElement = node;
            }}
            className={printableAreaClassName}
            style={style}>
            <div className={centerLineClassName} />

            {this.renderIcons()}
            {this.renderSelectionBoundingBox()}
            {this.renderBoundingBoxes()}
          </div>
        </div>
      );
    } else {
      return (
        <div className="ndx-Design">
          <div className={printableAreaClassName} style={style}>
            {this.renderIcons()}
          </div>
        </div>
      );
    }
  }
}
